import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, Mail, Phone, Loader2, RefreshCw } from 'lucide-react';

const MessageHistory = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [searchName, setSearchName] = useState('');
  const perPage = 15;

  useEffect(() => {
    fetchMessages();
  }, [page, filterType]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      let url = `${API}/rh/messages?page=${page}&per_page=${perPage}`;
      if (filterType !== 'all') url += `&msg_type=${filterType}`;
      if (searchName.trim()) url += `&search=${encodeURIComponent(searchName.trim())}`;
      const res = await axios.get(url);
      setMessages(res.data.messages);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchMessages();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const totalPages = Math.ceil(total / perPage);

  const getStatusBadge = (status) => {
    const map = {
      delivered: { label: 'Entregue', cls: 'bg-emerald-100 text-emerald-700' },
      sent: { label: 'Enviado', cls: 'bg-blue-100 text-blue-700' },
      saved: { label: 'Salvo', cls: 'bg-amber-100 text-amber-700' },
      failed: { label: 'Falhou', cls: 'bg-red-100 text-red-700' },
      pending: { label: 'Pendente', cls: 'bg-slate-100 text-slate-600' }
    };
    const s = map[status] || map.pending;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
  };

  const getTypeBadge = (type, direction) => {
    if (type === 'whatsapp') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1"><Phone className="w-3 h-3" /> WhatsApp</span>;
    if (type === 'contact') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 flex items-center gap-1"><Mail className="w-3 h-3" /> Contato</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{type}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="messages-history">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Mensagens</h2>
          <p className="text-slate-500 text-sm">Histórico de comunicações com colaboradores</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchMessages} data-testid="refresh-messages-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'Todos' },
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'contact', label: 'Contato' }
          ].map((f) => (
            <Button
              key={f.value}
              variant={filterType === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFilterType(f.value); setPage(1); }}
              data-testid={`filter-${f.value}`}
              className={filterType === f.value ? 'bg-slate-900 text-white' : ''}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-2 flex-1 max-w-sm">
          <Input
            placeholder="Buscar por nome..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={handleKeyDown}
            data-testid="search-messages-input"
            className="text-sm"
          />
          <Button variant="outline" size="sm" onClick={handleSearch} data-testid="search-messages-btn">
            Buscar
          </Button>
        </div>
      </div>

      {messages.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Nenhuma mensagem ainda</p>
            <p className="text-slate-400 text-sm mt-1">As mensagens enviadas via WhatsApp e contatos dos alunos aparecerão aqui</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="messages-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Tipo</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Contato</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Mensagem</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase hidden md:table-cell">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => (
                    <tr key={msg.message_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`message-row-${msg.message_id}`}>
                      <td className="px-4 py-3">{getTypeBadge(msg.type, msg.direction)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-sm text-slate-800">{msg.to_name || msg.from_name}</div>
                        <div className="text-xs text-slate-400">{msg.to_phone || msg.from_email || ''}</div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <p className="text-sm text-slate-600 truncate max-w-xs">{msg.body || msg.subject}</p>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(msg.status)}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-slate-500">
                          {new Date(msg.sent_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <span className="text-sm text-slate-500">{total} mensagem(ns)</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                  <span className="text-sm text-slate-600 flex items-center px-2">{page}/{totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Próximo</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};



export default MessageHistory;
